import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ApiCallError, callFunction } from '@/lib/api';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import toast from 'react-hot-toast';
import { Smartphone, ChevronRight, Globe } from 'lucide-react';

const ROLE_LABELS: Record<string, { da: string; en: string }> = {
  main_clinic_admin: { da: 'Hoved-administrator', en: 'Main administrator' },
  clinic_admin: { da: 'Administrator', en: 'Administrator' },
  employee: { da: 'Medarbejder', en: 'Employee' },
};

export function AccountSecurity() {
  const { t, i18n } = useTranslation();
  const { user, memberships, primaryClinicId, isPlatformAdmin } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (next.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (next !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }
    if (next === current) {
      setError(t('account.security.same_password'));
      return;
    }

    setSubmitting(true);
    try {
      await callFunction<{ current_password: string; new_password: string }, { changed: true }>(
        'change-password',
        {
          current_password: current,
          new_password: next,
        },
      );
      toast.success(t('account.security.changed_toast'));
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'unauthorized') {
          setError(t('account.security.wrong_current'));
        } else if (err.code === 'invalid-input') {
          setError(t('account.security.invalid'));
        } else {
          setError(err.message || t('auth.errors.unknown'));
        }
      } else {
        setError(err instanceof Error ? err.message : t('auth.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const lang = i18n.language === 'en' ? 'en' : 'da';

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('account.security.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
      </header>

      <section className="rounded-lg border border-gray-200 bg-card-light p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">
          {t('account.security.active_sessions', 'Active Sessions')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('account.security.active_sessions_hint', 'View and manage devices with access to your account')}
        </p>
        <Link
          to="/account/sessions"
          className="mt-4 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
              <Smartphone className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white">
                {t('account.security.manage_sessions', 'Manage Sessions')}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {t('account.security.view_devices', 'View and log out devices')}
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </Link>
      </section>

      <section className="rounded-lg border border-gray-200 bg-card-light p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">
          {t('account.security.change_password')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('account.security.change_password_hint')}
        </p>

        {error && (
          <div className="mt-4">
            <ErrorAlert message={error} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <Input
            label={t('account.security.current_password')}
            name="current"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <Input
            label={t('account.security.new_password')}
            name="new"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <Input
            label={t('account.security.confirm_password')}
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? t('account.security.saving') : t('account.security.save')}
            </Button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-gray-200 bg-card-light p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
            <Globe className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('account.security.language')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('account.security.language_hint')}
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={() => {
              void i18n.changeLanguage('da');
              toast.success(t('account.security.language_saved'));
            }}
            className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
              i18n.language !== 'en'
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-750'
            }`}
          >
            🇩🇰 {t('account.security.language_da')}
          </button>
          <button
            type="button"
            onClick={() => {
              void i18n.changeLanguage('en');
              toast.success(t('account.security.language_saved'));
            }}
            className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
              i18n.language === 'en'
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-900/30 dark:text-indigo-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-750'
            }`}
          >
            🇬🇧 {t('account.security.language_en')}
          </button>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-card-light p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">
          {t('account.security.memberships')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('account.security.memberships_hint')}
        </p>

        {isPlatformAdmin && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
            {t('account.security.platform_admin_badge')}
          </div>
        )}

        {memberships.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {t('account.security.no_memberships')}
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-700">
            {memberships.map((m) => (
              <li key={m.clinic_id} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <div className="font-mono text-gray-900 dark:text-gray-100">
                    {m.clinic_id.slice(0, 8)}…
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {ROLE_LABELS[m.role]?.[lang] ?? m.role}
                  </div>
                </div>
                {primaryClinicId === m.clinic_id && (
                  <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                    {t('account.security.primary_clinic')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
